import {ChangeDetectionStrategy, Component, Injectable, signal, Signal} from "@angular/core";
import {MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {AksjeeierbokId, Aksjeeiertype, GruppeModell, OpprettAksjeeierSpesifikasjon, OpprettMedlemInvitasjonSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AksjeeierDatatjeneste} from "felles-ng2/tjenester/datalager/AksjeeierDatatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatIconModule} from "@angular/material/icon";
import {AksjeeierRedigerKomponent} from "web-ng2/moduler/aksjeeierbok/komponenter/aksjeeier-rediger/aksjeeier-rediger";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {TranslateModule} from "@ngx-translate/core";
import {skrivPlainDateSerialisertOpt} from "felles-ng2/støttefunksjoner/temporal";
import {VisDersomIkkeScrolletTilBunnDirektiv} from "web-ng2/delt/direktiver/visDersomIkkeScrolletTilBunn/visDersomIkkeScrolletTilBunn";

const debug = initDebugger('OpprettAksjeeierDialogKomponent');

@Component({
    selector: 'opprett-aksjeeier-dialog',
    templateUrl: 'opprett-aksjeeier-dialog.html',
    styleUrls: ['opprett-aksjeeier-dialog.scss'],
    imports: [
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        AksjeeierRedigerKomponent,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        TranslateModule,
        VisDersomIkkeScrolletTilBunnDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpprettAksjeeierDialogKomponent {

    //@todo gruppe kunne vært tilgjengeliggjort på samme vis som aktivAksjeeierbok - via datatjeneste - muligens som en observable, men det er ikke essensielt da vi ikke skifter gruppen innefor tidsvinduet av en modal
    static vis(dialog: MatDialog): MatDialogRef<OpprettAksjeeierDialogKomponent, void> {
        return dialog.open(OpprettAksjeeierDialogKomponent);
    }

    skjema = AksjeeierRedigerKomponent.opprettSkjema();
    gruppe: Signal<GruppeModell>;
    lagrer = signal(false);

    constructor(private avhengigheter: OpprettAksjeeierDialogKomponentAvhengigheter,
                public dialogRef: MatDialogRef<OpprettAksjeeierDialogKomponent>) {
        this.gruppe = avhengigheter.aktivGruppe();
        this.skjema.patchValue(AksjeeierRedigerKomponent.nyTomPerson());
    }

    avbryt(): void {
        this.dialogRef.close();
    }

    lagre() {
        this.lagrer.set(true);
        this.avhengigheter.opprett(this.gruppe().aksjeeierbokId!, this.modellSomSpesifikasjon())
          .then(()=> this.dialogRef.close())
          .finally(() => this.lagrer.set(false));
    }

    //private hjelpemetoder

    private modellSomSpesifikasjon(): OpprettAksjeeierSpesifikasjon {
        return {
            id: this.skjema.value.id!,
            type: this.skjema.value.type!,
            navn: this.skjema.value.navn!,
            adresse: this.skjema.value.adresse!,
            epost: trimTilNull(this.skjema.value.epost),
            foedselsdato: this.skjema.value.type == Aksjeeiertype.person ? skrivPlainDateSerialisertOpt(this.skjema.value.foedselsdato) : null,
            organisasjonsnummer: this.skjema.value.type == Aksjeeiertype.organisasjon ? trimTilNull(this.skjema.value.organisasjonsnummer) : null,
            representertAv: this.skjema.value.erNyInvitasjon ? null : (this.skjema.value.representertAv?.id ?? null),
            invitasjon: this.skjema.value.erNyInvitasjon ? this.somInvitasjonSpesifikasjon() : null
        };
    }

    private somInvitasjonSpesifikasjon(): OpprettMedlemInvitasjonSpesifikasjon | null {
        const invSkjema = this.skjema.controls.nyInvitasjonSkjema.getRawValue();
        if (!invSkjema.epost) return null;
        return {
            epost: invSkjema.epost,
            navn: trimTilNull(invSkjema.navn),
            mobilnummer: trimTilNull(invSkjema.mobilnummer),
            rolleId: invSkjema.rolle?.id ?? null,
            sendInvitasjon: this.skjema.value.sendInvitasjon ?? false
        };
    }
}


@Injectable()
export class OpprettAksjeeierDialogKomponentAvhengigheter {

    constructor(private aksjeeierDatatjeneste: AksjeeierDatatjeneste,
                private gruppeDatatjeneste: GruppeDatatjeneste) {
    }

    aktivGruppe(): Signal<GruppeModell> {
        //merk at vi forventer at aktivGruppe er satt via annen kode
        return this.gruppeDatatjeneste.aktivGruppeIkkeNull;
    }

    opprett(aksjeeierbokId: AksjeeierbokId, spesifikasjon: OpprettAksjeeierSpesifikasjon): Promise<any> {
        return this.aksjeeierDatatjeneste.opprett(aksjeeierbokId, spesifikasjon);
    }
}
